import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Autocomplete from "@mui/material/Autocomplete";
import type {
  TaskListFilters as FiltersType,
  StatusFilterValue,
  TaskTypeValue,
} from "./useTaskListSubscription";
import { EMPTY_FILTERS } from "./useTaskListSubscription";
import { useTaskListRegions } from "./useTaskListRegions";
import { activityToDeactivated } from "./ActivityToDeactivated";

interface TaskListFiltersProps {
  onChange: (filters: FiltersType) => void;
}

const DEBOUNCE_MS = 400;
const CONTROL_HEIGHT = 40;

type StatusToggle = "all" | StatusFilterValue;

const STATUS_OPTIONS: { value: StatusToggle; label: string }[] = [
  { value: "all", label: "Все" },
  { value: "in_progress", label: "В работе" },
  { value: "completed", label: "Завершённые" },
  { value: "failed", label: "С ошибками" },
];

const TASK_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Все типы" },
  { value: "generation_anchor_topic", label: "Anchor Topic" },
  { value: "generation_relevant_topic", label: "Relevant Topic" },
  { value: "generation_relevant_topic_fixed", label: "Relevant Topic (Fixed)" },
  { value: "homepage_publication", label: "Homepage" },
  { value: "sitewide_publication", label: "Sitewide" },
];

const ACTIVITY_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Все" },
  { value: "active", label: "Активные" },
  { value: "deactivated", label: "Деактивированные" },
];

export function TaskListFilters(props: TaskListFiltersProps) {
  const { onChange } = props;
  const [local, setLocal] = useState<FiltersType>(EMPTY_FILTERS);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const regionsState = useTaskListRegions();
  const regionOptions = regionsState.kind === "loaded" ? regionsState.regions : [];

  const updateField = <K extends keyof FiltersType>(field: K, value: FiltersType[K]) => {
    setLocal((previous) => ({ ...previous, [field]: value }));
  };

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onChangeRef.current(local);
    }, DEBOUNCE_MS);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [local]);

  const handleStatusToggle = (_: React.MouseEvent<HTMLElement>, value: StatusToggle | null) => {
    const next = value ?? "all";
    updateField("statusFilter", next === "all" ? undefined : next);
  };

  const handleReset = () => {
    setLocal(EMPTY_FILTERS);
    onChangeRef.current(EMPTY_FILTERS);
  };

  const statusToggle: StatusToggle = local.statusFilter ?? "all";
  const activityWhenSet = local.deactivated === true ? "deactivated" : "active";
  const activityValue = local.deactivated === undefined ? "" : activityWhenSet;
  const hasActiveFilters =
    local.anchorTextFilter !== "" ||
    local.targetUrlFilter !== "" ||
    local.deactivated !== undefined ||
    local.regionFilter.length > 0 ||
    local.statusFilter !== undefined ||
    local.taskTypeFilter !== undefined;

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
        <Box flex="1 1 160px" maxWidth={220}>
          <TextField fullWidth
            size="small"
            label="Поиск по анкору"
            value={local.anchorTextFilter}
            onChange={(event) => updateField("anchorTextFilter", event.target.value)}
          />
        </Box>
        <Box flex="1 1 160px" maxWidth={220}>
          <TextField fullWidth
            size="small"
            label="Поиск по URL"
            value={local.targetUrlFilter}
            onChange={(event) => updateField("targetUrlFilter", event.target.value)}
          />
        </Box>
        <Box flex="1 1 150px" maxWidth={200}>
          <FormControl fullWidth size="small">
            <InputLabel>Тип задачи</InputLabel>
            <Select
              label="Тип задачи"
              value={local.taskTypeFilter ?? ""}
              onChange={(event) => {
                const outcome = event.target.value as string;
                updateField("taskTypeFilter", outcome === "" ? undefined : (outcome as TaskTypeValue));
              }}
            >
              {TASK_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box flex="1 1 200px" maxWidth={300}>
          <Autocomplete fullWidth
            multiple
            size="small"
            limitTags={1}
            options={regionOptions}
            value={local.regionFilter}
            onChange={(_event, value) => updateField("regionFilter", value)}
            renderInput={(parameters) => <TextField {...parameters} label="Регион" />}
          />
        </Box>
        <Box flex="1 1 140px" maxWidth={200}>
          <FormControl fullWidth size="small">
            <InputLabel>Активность</InputLabel>
            <Select
              label="Активность"
              value={activityValue}
              onChange={(event) => updateField("deactivated", activityToDeactivated(event.target.value as string))}
            >
              {ACTIVITY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          color="primary"
          value={statusToggle}
          onChange={handleStatusToggle}
          sx={{ height: CONTROL_HEIGHT, "& .MuiToggleButton-root": { textTransform: "none", px: 1.5 } }}
        >
          {STATUS_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {hasActiveFilters && (
          <Button size="small" onClick={handleReset} sx={{ ml: "auto" }}>
            Сбросить фильтры
          </Button>
        )}
      </Box>
    </Box>
  );
}
